import { UsageManagementService } from "@/lib/services/usage-management";
import { supabase } from "@/lib/supabase";

// Helper to get start/end of today in ISO format
function getTodayRange() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
}

export async function friendUsageGuard<T>({
  user,
  featureId,
  requestData,
  callApi,
  responseSummary,
  tokensUsed,
  costEstimate,
}: {
  user: { id: string; email: string };
  featureId: string;
  requestData: Record<string, unknown>;
  callApi: () => Promise<T>;
  responseSummary?: string;
  tokensUsed?: number;
  costEstimate?: number;
}): Promise<T> {
  // 1. Check if user is a friend invitee
  const invite = await UsageManagementService.getUserFriendInvite(user.email);
  if (!invite) {
    // Not a friend invitee, proceed as normal
    return await callApi();
  }

  // 2. Count today's usage for this feature
  const { start, end } = getTodayRange();
  const { data: usage, error } = await supabase
    .from("friend_testing_usage")
    .select("id")
    .eq("user_id", user.id)
    .eq("invite_id", invite.id)
    .eq("feature_id", featureId)
    .gte("created_at", start)
    .lte("created_at", end);

  if (usage && usage.length >= invite.daily_limit) {
    throw new Error("Daily usage limit reached");
  }

  // 3. Call the API/AI agent
  const response = await callApi();

  // 4. Track usage
  await UsageManagementService.trackFriendTestingUsage(
    user.id,
    invite.id,
    featureId,
    requestData,
    responseSummary,
    tokensUsed,
    costEstimate
  );

  return response;
}
